// ===========================================
// Route Optimization Utility
// Pure functions — no React dependencies
// ===========================================

export interface LatLng {
  lat: number;
  lng: number;
}

export interface RouteStop {
  index: number;          // Original index in the stops array
  coords: LatLng | null;  // null if no coordinates available
  label: string;          // For Google Maps URL (address or customer name)
}

export interface OptimizedRoute {
  orderedIndices: number[];     // Original indices in optimized visit order
  totalDistanceKm: number;     // Estimated total distance
  googleMapsUrl: string | null; // Multi-stop directions URL (coords-based, null if <2 stops with coords)
  googleMapsAddressUrl: string | null; // Fallback URL built from address labels (works even without coords)
  isOptimized: boolean;        // true if route was actually reordered by coordinates
}

const EARTH_RADIUS_KM = 6371;

/**
 * Haversine distance between two points in km.
 */
export function haversineDistance(a: LatLng, b: LatLng): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h = sinLat * sinLat + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinLng * sinLng;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}

/**
 * Builds a Google Maps multi-stop directions URL.
 * Opens natively on mobile devices.
 */
export function buildGoogleMapsUrl(
  origin: LatLng | null,
  stops: Array<{ coords: LatLng; label: string }>
): string | null {
  if (stops.length === 0) return null;

  const waypoints: string[] = [];

  if (origin) {
    waypoints.push(`${origin.lat},${origin.lng}`);
  }

  for (const stop of stops) {
    waypoints.push(`${stop.coords.lat},${stop.coords.lng}`);
  }

  if (waypoints.length < 2) return null;

  return `https://www.google.com/maps/dir/${waypoints.join('/')}`;
}

/**
 * Builds a Google Maps directions URL from address strings.
 * Fallback when coordinates are unavailable — Google geocodes the addresses.
 */
export function buildGoogleMapsAddressUrl(
  originAddress: string | null,
  stopAddresses: string[]
): string | null {
  const waypoints: string[] = [];

  if (originAddress) {
    waypoints.push(encodeURIComponent(originAddress));
  }

  for (const addr of stopAddresses) {
    if (addr) {
      waypoints.push(encodeURIComponent(addr));
    }
  }

  if (waypoints.length < 2) return null;

  return `https://www.google.com/maps/dir/${waypoints.join('/')}`;
}

/**
 * Builds a Google Maps embed URL for displaying a route preview in an iframe.
 * Uses the classic maps.google.com embed format — no API key required.
 * Origin should be the warehouse (planning view); live GPS is used only for navigation.
 *
 * Format: maps.google.com/maps?saddr=ORIGIN&daddr=DEST+to:WP1+to:WP2&output=embed
 */
export function buildGoogleMapsEmbedUrl(
  origin: LatLng | null,
  stops: Array<{ coords: LatLng | null; label: string }>
): string | null {
  if (stops.length === 0) return null;

  // Build ordered waypoint strings: prefer coords, fall back to address label
  const points: string[] = [];
  for (const stop of stops) {
    if (stop.coords) {
      points.push(`${stop.coords.lat},${stop.coords.lng}`);
    } else if (stop.label) {
      points.push(stop.label);
    }
  }
  if (points.length === 0) return null;

  // Origin: warehouse coords or first stop
  const saddr = origin ? `${origin.lat},${origin.lng}` : points.shift()!;
  if (points.length === 0) return null;

  // Build manually to avoid URLSearchParams encoding the +to: separators
  const saddrEnc = encodeURIComponent(saddr);
  const daddrRaw = points.map(p => encodeURIComponent(p)).join('+to:');

  return `https://maps.google.com/maps?saddr=${saddrEnc}&daddr=${daddrRaw}&output=embed`;
}

/**
 * Builds a Google Maps directions URL that uses the device's current GPS position
 * as the live origin. On mobile, this opens the native Maps app with turn-by-turn nav.
 * On desktop, Google Maps will use the browser's location if permission is granted.
 */
export function buildLiveNavigationUrl(
  stops: Array<{ coords: LatLng | null; label: string }>
): string | null {
  if (stops.length === 0) return null;

  // Build waypoints from coords (preferred) or address labels (fallback)
  const waypoints: string[] = [];
  for (const stop of stops) {
    if (stop.coords) {
      waypoints.push(`${stop.coords.lat},${stop.coords.lng}`);
    } else if (stop.label) {
      waypoints.push(stop.label);
    }
  }

  if (waypoints.length === 0) return null;

  const destination = waypoints.pop()!;
  const via = waypoints.join('|');

  // Using google.com/maps/dir/?api=1 format — origin omitted so Maps uses device GPS
  const params = new URLSearchParams({
    api: '1',
    travelmode: 'driving',
    destination,
    ...(via && { waypoints: via }),
  });

  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

/**
 * Nearest-neighbor TSP solver.
 * Sufficient for 5-15 daily stops (O(n^2) = ~225 ops max).
 *
 * - Origin = warehouse coords, driver GPS, or first stop with coords
 * - Stops without coordinates are appended at the end in original API order
 */
export function optimizeRoute(
  origin: LatLng | null,
  stops: RouteStop[],
  warehouseAddress?: string | null
): OptimizedRoute {
  // Build address-based fallback URL from all stops (in whatever order they end up)
  const buildAddressFallback = (orderedStops: RouteStop[]) => {
    return buildGoogleMapsAddressUrl(
      warehouseAddress || null,
      orderedStops.map(s => s.label).filter(Boolean)
    );
  };

  if (stops.length === 0) {
    return { orderedIndices: [], totalDistanceKm: 0, googleMapsUrl: null, googleMapsAddressUrl: null, isOptimized: false };
  }

  // Partition stops into those with and without coordinates
  const withCoords: RouteStop[] = [];
  const withoutCoords: RouteStop[] = [];

  for (const stop of stops) {
    if (stop.coords) {
      withCoords.push(stop);
    } else {
      withoutCoords.push(stop);
    }
  }

  // If no stops have coordinates, return original order with address-based fallback URL
  if (withCoords.length === 0) {
    return {
      orderedIndices: stops.map(s => s.index),
      totalDistanceKm: 0,
      googleMapsUrl: null,
      googleMapsAddressUrl: buildAddressFallback(stops),
      isOptimized: false,
    };
  }

  // Determine starting point
  let currentPos: LatLng = origin ?? withCoords[0].coords!;

  // Nearest-neighbor greedy ordering
  const visited = new Set<number>();
  const ordered: RouteStop[] = [];
  let totalDistance = 0;

  while (visited.size < withCoords.length) {
    let bestIdx = -1;
    let bestDist = Infinity;

    for (let i = 0; i < withCoords.length; i++) {
      if (visited.has(i)) continue;
      const dist = haversineDistance(currentPos, withCoords[i].coords!);
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = i;
      }
    }

    visited.add(bestIdx);
    ordered.push(withCoords[bestIdx]);
    totalDistance += bestDist;
    currentPos = withCoords[bestIdx].coords!;
  }

  // Append stops without coordinates at end (original API order preserved)
  const allOrdered = [...ordered, ...withoutCoords];

  // Build Google Maps URL from ordered stops with coords
  const mapsStops = ordered.map(s => ({ coords: s.coords!, label: s.label }));
  const googleMapsUrl = buildGoogleMapsUrl(origin, mapsStops);

  return {
    orderedIndices: allOrdered.map(s => s.index),
    totalDistanceKm: Math.round(totalDistance * 10) / 10,
    googleMapsUrl,
    googleMapsAddressUrl: buildAddressFallback(allOrdered),
    isOptimized: true,
  };
}
